import { useEffect, useState, useCallback, useMemo } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import ChatIcon from '@mui/icons-material/Chat';
import { McpIcon } from '../components/icons';
import ListSortFilterBar from '../components/ListSortFilterBar';
import RequestAccessDialog from '../components/RequestAccessDialog';
import ServerCard from '../components/ServerCard';
import UpgradeBanner from '../components/UpgradeBanner';
import Alert from '@mui/material/Alert';
import { api } from '../api';
import { PortalServerOut as PortalServer, ChatUrlOut as ChatUrl } from '../types/apiRequests';
import { useOrgInfo, useRefreshOrgInfo } from '../contexts/OrgInfoContext';
import { useBeheerder } from '../hooks/useBeheerder';
import { useTranslation } from '../i18n/useTranslation';

type SortMode = 'activated' | 'name_asc' | 'name_desc' | 'newest' | 'oldest';
const SORT_KEY = 'sa_portal_sort';
const FILTER_KEY = 'sa_portal_app_filter';

const ACCESS_ORDER: Record<string, number> = {
  full_access: 0,
  instant_available: 1,
  org_linked_no_user: 2,
  request_pending: 3,
  not_linked: 4,
};

export default function ServersPage() {
  const { getAccessTokenSilently } = useOidcContext();
  const navigate = useNavigate();
  const orgInfo = useOrgInfo();
  const refreshOrgInfo = useRefreshOrgInfo();
  const isBeheerder = useBeheerder();
  const t = useTranslation();
  const [servers, setServers] = useState<PortalServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState<SortMode>(() => {
    const stored = localStorage.getItem(SORT_KEY);
    const validModes: SortMode[] = ['activated', 'name_asc', 'name_desc', 'newest', 'oldest'];
    return stored && validModes.includes(stored as SortMode) ? (stored as SortMode) : 'activated';
  });
  const [hiddenApps, setHiddenApps] = useState<string[]>(
    () => {
      try { return JSON.parse(localStorage.getItem(FILTER_KEY) || '[]'); }
      catch { return []; }
    },
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedServer, setSelectedServer] = useState<PortalServer | null>(null);
  const [chatUrl, setChatUrl] = useState<ChatUrl | null>(null);
  const [requestingTier, setRequestingTier] = useState<string | null>(null);
  const [requestedTier, setRequestedTier] = useState<string | null>(null);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadServers = useCallback(async () => {
    try {
      setLoadError(null);
      const token = await getAccessTokenSilently();
      const [serversData, chatData] = await Promise.all([
        api.getServers(token),
        api.getChatUrl(token),
      ]);
      setServers(serversData);
      setChatUrl(chatData);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : t('errors.genericError'));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, t]);

  useEffect(() => { loadServers(); }, [loadServers]);

  // Refresh when the tab becomes visible again (returning from another tab or
  // an unminimised window). We deliberately use `visibilitychange` instead of
  // the window `focus` event: a bare window focus also fires when you click
  // back into an already-visible window from another desktop app, and the
  // browser's click-to-focus then swallows that first click — so a nav-link
  // click would just trigger a refetch instead of navigating. Gating on the
  // visibility state avoids that confusing "it refreshed instead of navigating".
  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshOrgInfo();
        loadServers();
      }
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  }, [loadServers, refreshOrgInfo]);

  const handleInstantAdd = useCallback(async (server: PortalServer) => {
    if ((server.environments ?? []).length > 0 && !server.default_environment_name) {
      navigate(`/servers/${server.name}?action=request-access`);
      return;
    }
    try {
      const token = await getAccessTokenSilently();
      await api.instantAdd(token, server.name, server.default_environment_name ?? undefined);
      refreshOrgInfo();
      loadServers();
    } catch (e: unknown) {
      setUpgradeError(e instanceof Error ? e.message : t('errors.requestFailed'));
    }
  }, [navigate, getAccessTokenSilently, refreshOrgInfo, loadServers, t]);

  const handleRequestAccess = useCallback((server: PortalServer) => {
    setSelectedServer(server);
    setDialogOpen(true);
  }, []);

  const handleSubmitRequest = useCallback(async (data: { request_type: string; environment_url?: string; environment_name?: string; message?: string }) => {
    if (!selectedServer) return;
    const token = await getAccessTokenSilently();
    await api.requestAccess(token, selectedServer.name, data);
    setDialogOpen(false);
    refreshOrgInfo();
    loadServers();
  }, [selectedServer, getAccessTokenSilently, refreshOrgInfo, loadServers]);

  const handleRequestTier = useCallback(async (tier: string) => {
    if (requestingTier || requestedTier) return;
    setRequestingTier(tier);
    setUpgradeError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.requestPlatformTier(token, tier);
      setRequestedTier(tier);
    } catch (e: unknown) {
      setUpgradeError(e instanceof Error ? e.message : t('errors.requestFailed'));
    } finally {
      setRequestingTier(null);
    }
  }, [requestingTier, requestedTier, getAccessTokenSilently, t]);

  const slotLimitReached =
    orgInfo !== null && orgInfo.mcp_slots > 0 && orgInfo.mcp_slots_used >= orgInfo.mcp_slots;

  const effectiveSort = (!isBeheerder && sortMode === 'activated') ? 'name_asc' : sortMode;

  const appNames = useMemo(
    () => Array.from(new Set(servers.map((s) => s.application_name).filter((n): n is string => !!n))).sort(),
    [servers],
  );

  const sortedServers = useMemo(() => {
    const filtered = hiddenApps.length > 0
      ? servers.filter((s) => !hiddenApps.includes(s.application_name || ''))
      : servers;

    return [...filtered].sort((a, b) => {
      if (effectiveSort === 'activated') {
        const orderA = ACCESS_ORDER[a.access_status] ?? 9;
        const orderB = ACCESS_ORDER[b.access_status] ?? 9;
        if (orderA !== orderB) return orderA - orderB;
        return a.display_name.localeCompare(b.display_name);
      }
      if (effectiveSort === 'name_asc') return a.display_name.localeCompare(b.display_name);
      if (effectiveSort === 'name_desc') return b.display_name.localeCompare(a.display_name);
      if (effectiveSort === 'newest') {
        return (b.created || '').localeCompare(a.created || '');
      }
      if (effectiveSort === 'oldest') {
        return (a.created || '').localeCompare(b.created || '');
      }
      return 0;
    });
  }, [servers, effectiveSort, hiddenApps]);

  const handleSortChange = (mode: SortMode) => {
    setSortMode(mode);
    localStorage.setItem(SORT_KEY, mode);
  };

  const handleToggleApp = (appName: string) => {
    const next = hiddenApps.includes(appName)
      ? hiddenApps.filter((a) => a !== appName)
      : [...hiddenApps, appName];
    setHiddenApps(next);
    localStorage.setItem(FILTER_KEY, JSON.stringify(next));
  };

  const visibleAppCount = appNames.length - hiddenApps.filter((a) => appNames.includes(a)).length;
  const filterLabel = visibleAppCount === appNames.length
    ? t('servers.filter.allApps')
    : visibleAppCount === 1
      ? appNames.find((a) => !hiddenApps.includes(a)) || ''
      : `${visibleAppCount} ${t('servers.filter.selected')}`;

  const sortOptions = [
    ...(isBeheerder ? [{ value: 'activated', label: t('servers.sort.activated') }] : []),
    { value: 'name_asc', label: t('servers.sort.nameAsc') },
    { value: 'name_desc', label: t('servers.sort.nameDesc') },
    { value: 'newest', label: t('servers.sort.newest') },
    { value: 'oldest', label: t('servers.sort.oldest') },
  ];

  return (
    <>
      <Box sx={{ mb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h3" sx={{ mb: 1 }}>
            {t('servers.page.title')}
          </Typography>
          <Typography variant="subtitle1">
            {t('servers.page.subtitle')}
          </Typography>
        </Box>
        {orgInfo?.chat_enabled === true && (
          orgInfo?.hub_tier === 'enterprise' && chatUrl?.enabled && chatUrl.url ? (
            <Button
              variant="contained"
              startIcon={<ChatIcon />}
              href={chatUrl.url}
              target="_blank"
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                px: 3,
              }}
            >
              {t('servers.chat.button')}
            </Button>
          ) : (
            <Tooltip title={t('servers.chat.notConfiguredTooltip')}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<ChatIcon />}
                  disabled
                  sx={{
                    borderColor: 'rgba(0,0,0,0.12)',
                    color: 'text.disabled',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '10px',
                    px: 3,
                    '&.Mui-disabled': {
                      borderColor: 'rgba(0,0,0,0.08)',
                      color: '#c4c4c4',
                    },
                  }}
                >
                  {t('servers.chat.button')}
                </Button>
              </span>
            </Tooltip>
          )
        )}
      </Box>

      {isBeheerder && orgInfo && orgInfo.hub_tier !== 'none' && orgInfo.hub_tier !== 'enterprise' && (
        <UpgradeBanner
          orgInfo={orgInfo}
          requestingTier={requestingTier}
          requestedTier={requestedTier}
          upgradeError={upgradeError}
          onRequestTier={handleRequestTier}
        />
      )}

      {loadError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
          {loadError}
        </Alert>
      )}

      {servers.some((s) => s.blocked_until && new Date(s.blocked_until) > new Date()) && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
          {t('serverDetail.blocked', {
            until: new Date(
              servers.find((s) => s.blocked_until && new Date(s.blocked_until) > new Date())!.blocked_until!
            ).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          })}
        </Alert>
      )}

      {!loading && servers.length > 0 && (
        <ListSortFilterBar
          sortValue={effectiveSort}
          sortOptions={sortOptions}
          onSortChange={(v) => handleSortChange(v as SortMode)}
          filter={
            appNames.length > 1
              ? {
                  label: filterLabel,
                  options: appNames.map((name) => ({
                    value: name,
                    label: name,
                    checked: !hiddenApps.includes(name),
                  })),
                  onToggle: handleToggleApp,
                }
              : undefined
          }
        />
      )}

      <Grid container spacing={2.5}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ p: 3, height: 190 }}>
                  <Skeleton variant="circular" width={40} height={40} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="90%" sx={{ mt: 1 }} />
                  <Skeleton variant="rounded" width={100} height={22} sx={{ mt: 3 }} />
                </Card>
              </Grid>
            ))
          : sortedServers.map((server) => (
              <Grid item xs={12} sm={6} md={4} key={server.id}>
                <ServerCard
                  server={server}
                  isBeheerder={isBeheerder}
                  slotLimitReached={slotLimitReached}
                  onNavigate={(name) => navigate(`/servers/${name}`)}
                  onRequestAccess={handleRequestAccess}
                  onInstantAdd={handleInstantAdd}
                />
              </Grid>
            ))}
      </Grid>

      {!loading && servers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 12 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '16px',
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <McpIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
            {isBeheerder ? t('servers.empty.adminTitle') : t('servers.empty.userTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            {isBeheerder
              ? t('servers.empty.adminSubtitle')
              : t('servers.empty.userSubtitle')}
          </Typography>
        </Box>
      )}

      {selectedServer && (
        <RequestAccessDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmitRequest}
          serverName={selectedServer.display_name}
          hasDefaultUrl={selectedServer.has_default_url ?? false}
          environments={selectedServer.environments ?? []}
        />
      )}
    </>
  );
}
